import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiCalendar, FiList, FiEdit2, FiTrash2, FiCheck, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';

const MyTasks = () => {
  const { user, isAdmin } = useAuth();
  const { isDark } = useTheme();
  const { showSuccess, showError } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Admin: view own tasks vs everyone's tasks
  const [adminView, setAdminView] = useState('mine');
  const [allUsersTasks, setAllUsersTasks] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    reminder_date: '',
    is_recurring: false,
    recurrence_day_of_month: new Date().getDate(),
  });

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  useEffect(() => {
    if (isAdmin && adminView === 'everyone') {
      setAllUsersLoading(true);
      api.get('/my-tasks/all')
        .then((res) => setAllUsersTasks(res.data.users || []))
        .catch(() => showError('Failed to load everyone\'s tasks'))
        .finally(() => setAllUsersLoading(false));
    }
  }, [isAdmin, adminView]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      const url = `/my-tasks${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      showError('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await api.put(`/my-tasks/${editingTask.id}`, formData);
        showSuccess('Task updated successfully!');
      } else {
        const payload = { ...formData };
        if (formData.is_recurring) {
          payload.is_recurring = true;
          payload.recurrence_day_of_month = formData.recurrence_day_of_month;
        }
        await api.post('/my-tasks', payload);
        showSuccess(formData.is_recurring ? 'Recurring task created! A new instance will appear each month.' : 'Task created successfully!');
      }
      setShowModal(false);
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        reminder_date: '',
        is_recurring: false,
        recurrence_day_of_month: new Date().getDate(),
      });
      fetchTasks();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      due_date: task.due_date ? format(new Date(task.due_date), "yyyy-MM-dd'T'HH:mm") : '',
      reminder_date: task.reminder_date ? format(new Date(task.reminder_date), "yyyy-MM-dd'T'HH:mm") : '',
      is_recurring: false,
      recurrence_day_of_month: new Date().getDate(),
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/my-tasks/${id}`);
      showSuccess('Task deleted successfully!');
      fetchTasks();
    } catch (error) {
      showError('Failed to delete task');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/my-tasks/${id}/status`, { status });
      showSuccess(`Task marked as ${status.replace('_', ' ')}!`);
      fetchTasks();
    } catch (error) {
      showError('Failed to update status');
    }
  };

  const openNewTaskModal = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      reminder_date: '',
      is_recurring: false,
      recurrence_day_of_month: new Date().getDate(),
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: isDark ? 'bg-gray-600' : 'bg-gray-500',
      in_progress: isDark ? 'bg-blue-600' : 'bg-blue-500',
      completed: isDark ? 'bg-green-600' : 'bg-green-500',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: isDark ? 'bg-green-600' : 'bg-green-500',
      medium: isDark ? 'bg-yellow-600' : 'bg-yellow-500',
      high: isDark ? 'bg-red-600' : 'bg-red-500',
    };
    return colors[priority] || colors.medium;
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getTasksForDay = (day) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      return isSameDay(new Date(task.due_date), day);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:gap-4">
        <div>
          <h1 className={`text-2xl lg:text-3xl font-bold mb-1 lg:mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            My Tasks
          </h1>
          <p className={`text-sm lg:text-base ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
            {isAdmin && adminView === 'everyone'
              ? "Everyone's personal tasks (admin view)"
              : `Personal tasks and reminders for ${user?.name || 'you'}`}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAdminView('mine')}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                adminView === 'mine'
                  ? isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-primary-600 shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              My tasks
            </button>
            <button
              onClick={() => setAdminView('everyone')}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                adminView === 'everyone'
                  ? isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-primary-600 shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              Everyone's tasks
            </button>
          </div>
        )}
        {(!isAdmin || adminView === 'mine') && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={openNewTaskModal}
          className={`px-5 py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all shadow-lg w-fit ${
            isDark 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-white text-primary-600 active:bg-white/90'
          }`}
        >
          <FiPlus size={20} />
          <span>Add Task</span>
        </motion.button>
        )}
      </div>

      {/* Everyone's tasks (admin only) */}
      {isAdmin && adminView === 'everyone' && (
        <div className={`backdrop-blur-md rounded-xl border overflow-hidden ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/10 border-white/20'
        }`}>
          {allUsersLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`} />
            </div>
          ) : allUsersTasks.length === 0 ? (
            <p className={`py-8 text-center ${isDark ? 'text-gray-400' : 'text-white/60'}`}>No personal tasks from any user yet.</p>
          ) : (
            <div className="divide-y divide-white/10">
              {allUsersTasks.map(({ id: userId, name, email, tasks: userTasks }) => (
                <div key={userId} className="p-4 lg:p-6">
                  <h2 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-white'}`}>
                    {name}
                  </h2>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-white/60'}`}>{email}</p>
                  {userTasks.length === 0 ? (
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-white/50'}`}>No personal tasks</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-white/20'}`}>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Title</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Status</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Priority</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userTasks.map((task) => (
                            <tr key={task.id} className={`border-b ${isDark ? 'border-gray-700/50' : 'border-white/10'}`}>
                              <td className="px-3 py-2">
                                <span className={`${isDark ? 'text-white' : 'text-white'} flex items-center gap-2`}>
                                  {task.title}
                                  {task.recurrence_parent_id && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/30 text-indigo-200">Monthly</span>
                                  )}
                                </span>
                                {task.description && (
                                  <p className={`text-xs mt-0.5 line-clamp-1 ${isDark ? 'text-gray-400' : 'text-white/60'}`}>{task.description}</p>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`${getStatusColor(task.status)} text-white text-xs px-2 py-0.5 rounded-full capitalize`}>
                                  {task.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className={`px-3 py-2 capitalize ${isDark ? 'text-gray-300' : 'text-white/80'}`}>{task.priority}</td>
                              <td className={`px-3 py-2 ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
                                {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* View Toggle & Filters (only when viewing own tasks) */}
      {(!isAdmin || adminView === 'mine') && (
      <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
              viewMode === 'table'
                ? isDark ? 'bg-blue-600 text-white' : 'bg-white text-primary-600'
                : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <FiList size={18} />
            <span>Table</span>
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
              viewMode === 'calendar'
                ? isDark ? 'bg-blue-600 text-white' : 'bg-white text-primary-600'
                : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <FiCalendar size={18} />
            <span>Calendar</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'in_progress', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-xl font-medium transition-all capitalize ${
                filter === status
                  ? isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-primary-600 shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 active:bg-white/20'
              }`}
            >
              {status.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'calendar' ? (
        <div className={`backdrop-blur-md rounded-xl p-4 lg:p-6 border ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/10 border-white/20'
        }`}>
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'}`}
            >
              <FiChevronLeft size={24} className={isDark ? 'text-white' : 'text-white'} />
            </button>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'}`}
            >
              <FiChevronRight size={24} className={isDark ? 'text-white' : 'text-white'} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={`text-center py-2 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-white/60'}`}>
                {day}
              </div>
            ))}
            {daysInMonth.map((day, index) => {
              const dayTasks = getTasksForDay(day);
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={index}
                  className={`min-h-[80px] p-1 border rounded-lg ${
                    isDark ? 'border-gray-700' : 'border-white/10'
                  } ${isToday ? (isDark ? 'bg-blue-900/30' : 'bg-white/20') : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isToday 
                      ? (isDark ? 'text-blue-400' : 'text-white') 
                      : (isDark ? 'text-gray-300' : 'text-white/80')
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayTasks.slice(0, 2).map(task => (
                      <div
                        key={task.id}
                        onClick={() => handleEdit(task)}
                        className={`text-xs p-1 rounded truncate cursor-pointer ${getPriorityColor(task.priority)} text-white flex items-center gap-1`}
                      >
                        <span className="truncate">{task.title}</span>
                        {task.recurrence_parent_id && <span className="shrink-0 text-[10px] opacity-90">📅</span>}
                      </div>
                    ))}
                    {dayTasks.length > 2 && (
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-white/60'}`}>
                        +{dayTasks.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : tasks.length === 0 ? (
        <div className={`backdrop-blur-md rounded-xl p-12 border text-center ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/10 border-white/20'
        }`}>
          <p className={`text-lg mb-2 ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
            No personal tasks yet
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-white/60'}`}>
            Create your first personal task to get started
          </p>
        </div>
      ) : (
        <div className={`backdrop-blur-md rounded-xl border overflow-hidden ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/10 border-white/20'
        }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-white/20'}`}>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-white'}`}>Title</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-white'}`}>Status</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-white'}`}>Priority</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-white'}`}>Due Date</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-white'}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task, index) => (
                  <motion.tr
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`border-b hover:bg-opacity-50 transition-colors ${
                      isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-white/10 hover:bg-white/5'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-white'} flex items-center gap-2 flex-wrap`}>
                        {task.title}
                        {task.recurrence_parent_id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200">Monthly</span>
                        )}
                      </div>
                      {task.description && (
                        <p className={`text-xs mt-1 line-clamp-1 ${isDark ? 'text-gray-400' : 'text-white/60'}`}>
                          {task.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value)}
                        className={`${getStatusColor(task.status)} text-white text-xs px-2 py-1 rounded-full capitalize cursor-pointer border-0 outline-none`}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`${getPriorityColor(task.priority)} text-white text-xs px-2 py-1 rounded-full capitalize`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
                      {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(task)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-white/20 text-white/80'
                          }`}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(task.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark ? 'hover:bg-red-600/50 text-red-400' : 'hover:bg-red-500/30 text-red-300'
                          }`}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: '100%', scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: '100%', scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingTask ? 'Edit Task' : 'New Task'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter description"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                {!formData.is_recurring && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Due Date</label>
                  <input
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                )}

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Reminder</label>
                  <input
                    type="datetime-local"
                    value={formData.reminder_date}
                    onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">You'll receive an email reminder at this time</p>
                </div>

                {!editingTask && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-gray-700 text-sm font-medium">Repeat monthly (same date every month)</span>
                  </label>
                  {formData.is_recurring && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 text-sm">On day</span>
                      <select
                        value={formData.recurrence_day_of_month}
                        onChange={(e) => setFormData({ ...formData, recurrence_day_of_month: Number(e.target.value) })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <span className="text-gray-600 text-sm">of each month</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">A new task will open on that date each month; complete it for each month.</p>
                </div>
                )}

                <div className="flex items-center space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                  >
                    {editingTask ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyTasks;
